<template>
	<b-card class="mb-3">
		<b-row class="mb-3">
			<b-col cols="10">
				<h4>{{ heading }}</h4>
			</b-col>

			<b-col cols="2">
				<b-link
					class="float-right mb-3"
					title="Upload a new file"
					@click.prevent="chooseFile"
				>
					<b-icon font-scale="1.5" icon="plus-square"></b-icon>
				</b-link>
			</b-col>
		</b-row>
       
        <div v-if="assetLinks.length < assetCount">
         
            <b-progress class="w-100" :max="assetCount " height="1.5rem">
                <b-progress-bar :value=" assetLinks.length" :label="`${(( assetLinks.length / assetCount ) * 100).toFixed(2)}%`"></b-progress-bar>
            </b-progress>

        </div>

        <carousel v-if="assetLinks.length == assetCount "
			:scrollPerPage="true"
			:perPageCustom="[
				[480, 2],
				[768, 4],
				[1024, 5],
			]"
		>
			<!-- card for any type of asset, show image or embed video depending on type -->
			<slide v-for="(asset, index) in assetLinks" :key="index + 10">
				
                <b-col class="h-100">

					<!-- img or embed depending on video -->
					<div class="border rounded h-100  d-flex  flex-column">
						<b-img
							v-if="type != '2'"
							v-img
							fluid-grow
							:src="asset.source"
							:alt="asset.fileName"
						></b-img>

						<b-embed
							v-if="type == '2'"
							type="video"
							aspect="4by3"
							controls
							poster="poster.png"
						>
							<source :src="asset.source" type="video/webm" />
						</b-embed>

						<!-- info parts -->

						<div class="d-flex justify-content-center">
							<small>{{ asset.added }}</small>
						</div>

						<div class="mt-auto my-3 mx-3">
							<b-link
								class="mt-auto"
								title="Download file"
								@click.prevent="downloadFile(asset)"
							>
								<b-icon
									font-scale="1.5"
									icon="download"
								></b-icon>
							</b-link>

							<b-link
								class="mt-auto float-right"
								@click.prevent="deleteFile(index)"
								title="Delete file"
							>
								<b-icon
									font-scale="1.5"
									icon="trash"
									variant="danger"
								></b-icon>
							</b-link>
						</div>
					</div>
				</b-col>
			</slide>
		</carousel>
		<!-- uploads -->

		<input
			:accept="accept"
			type="file"
			class="invisible"
			ref="files"
			multiple
			@change.prevent="uploadFiles"
		/>

		<confirm ref="confirm"></confirm>
	</b-card>
</template>

<script>
import FileService from "@/api-services/file.service";
import Confirm from "@/components/partials/confirm";
import { dateFuncs } from "@/globs.js";
import Vue from "vue";
import VueImg from "v-img";

import { Carousel, Slide } from "vue-carousel";

Vue.use(VueImg);

const assetType = {
	1: { heading: "Images", accept: "image/*" },
	2: { heading: "Videos", accept: "video/*" },
};

export default {
	name: "FileAsset",

	components: { Confirm, Carousel, Slide },

	// things we have inherited from our parent
	props: ["assets", "type", "relate", "folder"],

	// data we bind to, components need a function wrapping this all up, to keep instances seperate
	data: function () {
		return {
            assetCount: null,
            filteredAssets : [],
			assetLinks: [],
			files: [],
            fullscreen: false,
		};
	},

	computed: {
		heading: function () {
			return assetType[this.type].heading;
		},

		accept: function () {
			return assetType[this.type].accept;
		},
	},

	created() {
		// if we get here and already have an array, then don't wait for a watch that may never come.
		if (this.assets.length) {
			var unwatch = this.$watch("assets");
			unwatch();
			this.fetchExistingAssets();
		}
	},

	// things we do to data
	methods: {
		chooseFile() {
			this.$refs.files.click();
		},

		// GET AND ENCODE ASSETS
		fetchExistingAssets() {
			// filter assets by type
			let asset = {};
			let assetType = this.assetType;
            
            this.filteredAssets = this.assets.filter((asset) => {
				return asset.AssetType == this.type;
            });
            
            this.assetCount = this.filteredAssets.length;


			this.filteredAssets.forEach((asset) => {
				switch (this.type) {
					case "1": // if it's an image we can stream it and encode in base64 to show thumbNail
						this.streamToBase64(asset);
						break;

					case "2": // if it's a video we can add the blob as the vid source in html5 video element
						this.getVideo(asset);
						break;
				}
			});
		},

		getDocument(_asset) {
			let assetLink = {
				guid: _asset.NanId,
				fileName: _asset.FileName,
				contentType: _asset.ContentType,
				added: dateFuncs.backEndDateFormat(_asset.Added),
				source:
					"https://svn.alfresco.com/repos/alfresco-open-mirror/alfresco/HEAD/root/projects/repository/config/alfresco/thumbnail/thumbnail_placeholder_256_doc.png",
			};
			this.assetLinks.push(assetLink);
		},

		getVideo(_asset) {
			FileService.get(_asset.NanId).then((response) => {
				const url = window.URL.createObjectURL(
					new Blob([response.data])
				);
				let assetLink = {
					guid: _asset.NanId,
					fileName: _asset.FileName,
					contentType: _asset.ContentType,
					added: dateFuncs.backEndDateFormat(_asset.Added),
					source: url,
				};

                this.assetLinks.push(assetLink);
                     
			});
		},

		streamToBase64(_asset) {
			FileService.getStream(_asset.NanId)
				.then((res) => {
					let assetLink = {
						guid: _asset.NanId,
						fileName: _asset.FileName,
						contentType: _asset.ContentType,
						added: dateFuncs.backEndDateFormat(_asset.Added),
						source: this.imageEncode(res.data),
					};
                    this.assetLinks.push(assetLink);
                    
				})
				.catch((error) => {
					this.handleError(error);
				});
		},

		// put the stream into a base64 encoded string ready for the src of the img element.
		imageEncode(arrayBuffer) {
			let b64encoded = btoa(
				[].reduce.call(
					new Uint8Array(arrayBuffer),
					function (p, c) {
						return p + String.fromCharCode(c);
					},
					""
				)
			);
			let mimetype = "image/jpeg";
			return "data:" + mimetype + ";base64," + b64encoded;
		},

		downloadFile(_asset) {
			FileService.get(_asset.guid)
				.then((response) => {
					const url = window.URL.createObjectURL(
						new Blob([response.data])
					);
					const link = document.createElement("a");
					link.href = url;
					link.setAttribute("download", _asset.fileName);
					document.body.appendChild(link);
					link.click();
				})
				.catch((error) => {
					this.handleError(error);
				});
		},

		/*
           UPLOAD NEW FILES
        */
		submitFiles() {
			//Initialize the form data
			let formData = new FormData();

			// Iterate over any file sent over appending the files to the form data.
			for (var i = 0; i < this.files.length; i++) {
				let file = this.files[i];
				formData.append("files[" + i + "]", file);
			}

			// add the type of file form prop
			formData.append("AssetType", this.type);
			formData.append("Folder", this.folder);

			// Make the request to the POST /multiple-files handler in api service
			FileService.upload(this.relate, formData)
				.then((res) => {
					res.data.forEach((asset, i) => {
						switch (this.type) {
							case "1": // if it's an image we can stream it and encode in base64 to show thumbNail
                                this.streamToBase64(asset);
                                
                                
                                   this.assetCount++;
								break;

							case "2": // if it's a video we can add the blob as the vid source in html5 video element
                                this.getVideo(asset);
                                
                                   this.assetCount++;
								break;
						}
					});

					this.$refs.confirm.open(
						"Success",
						"File(s) uploaded",
						true
					);
				})
				.catch((error) => {
					this.handleError(error);
				})
				.then(() => {
					this.$refs.files.value = "";
				});
		},

		uploadFiles() {
			this.files = this.$refs.files.files;
			this.submitFiles();
		},

		// Modal stuff
		deleteFile(index) {
			this.$refs.confirm
				.open("Delete", "Are you sure you want to delete this file?")
				.then((confirm) => {
					if (confirm) {
						let vm = this;

						let guid = this.assetLinks[index].guid;

						FileService.delete(this.folder, guid)
							.then(() => {
                                vm.$delete(vm.assetLinks, index);
                                
                                vm.assetCount--;
							})
							.catch((error) => {
								this.handleError(error);
							});
					}
				});
		},

		handleError(err) {
			let modal_options = {
				text: "light",
				background: "danger",
			};

			let message;

			if (err.response.data != '') {

				message = err.MessageDetail != null ? err.MessageDetail : err.ExceptionMessage;

			} else {

				switch (err.response.status) {
					case 404:
						message = err.response.status + ' - Could not find requested data';
						break;
					case 500:
						message = err.response.status + ' - There was a fatal error whilst processing this request';
						break;

					default:
						message = err.response.status + ' - There was an unknown error whilst processing this request';
				}
			}

			this.$refs.confirm.open('Error', message, true, modal_options);
			
		},
	},

	watch: {
		// Watch for the assets prop to be populated from ajax
		assets: function () {
			this.fetchExistingAssets();
		},
	},
};
</script>


// WEBPACK FOOTER //
// src/components/partials/file-assets.vue